import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { AppShell, Text, Burger, Button, Stack, Group, Title, Paper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconHome } from '@tabler/icons-react';
import { seniorRoute } from '../providers/seniorRoute';
import ErrorBoundary from '../components/ErrorBoundary';
import { 
  GlobalLoadingIndicator, 
  NetworkStatus, 
  KeyboardNavigation,
  AccessibilityEnhancer
} from '../components/UXEnhancements';
import '../styles/senior-friendly.css';
import '../styles/mobile-optimized.css';
import '../styles/mobile-responsive.css';

function SeniorFriendlyApp() {
  const location = useLocation();
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isLarge = useMediaQuery('(min-width: 1200px)');

  const menuItems = seniorRoute[1].children
    .filter(i => !i.hideMenu)
    .map(({ path, icon, title }) => {
      const IconComponent = icon || IconHome;
      const isActive = location.pathname === path;

      return (
        <Button
          key={path}
          component={Link}
          to={path}
          onClick={() => setOpened(false)}
          variant={isActive ? 'filled' : 'subtle'}
          color={isActive ? 'blue' : 'gray'}
          size="lg"
          leftSection={<IconComponent size={24} />}
          justify="flex-start"
          style={{
            fontSize: 'var(--mantine-font-size-sm)',
            fontWeight: '600',
            height: '60px',
            marginBottom: '8px'
          }}
          fullWidth
        >
          {title}
        </Button>
      );
    });

  return (
    <AccessibilityEnhancer>
      <KeyboardNavigation />
      <GlobalLoadingIndicator />
      <NetworkStatus />
      <AppShell
      header={{
        height: { base: 70, md: 0 }
      }}
      navbar={{
        width: { base: 280, md: 280, lg: 300 },
        breakpoint: 'md',
        collapsed: { mobile: !opened, desktop: false }
      }}
      footer={!isMobile ? { height: 60 } : undefined}
      padding={0}
      style={{ backgroundColor: '#f5f5f5' }}
    >
      {/* 手機版頂部導航 */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" hiddenFrom="md">
          <Title order={3} c="blue" style={{ fontSize: 'var(--mantine-font-size-lg)' }}>
            進貨統計系統
          </Title>
          <Burger opened={opened} onClick={() => setOpened(!opened)} size="lg" color="blue" />
        </Group>
      </AppShell.Header>

      {/* 側邊欄 */}
      <AppShell.Navbar p="md" style={{ backgroundColor: '#ffffff' }}>
        <Stack gap="md" h="100%">
          {/* 標題區域 */}
          <Paper
            p={isMobile ? 'md' : 'xl'}
            className={isMobile ? 'mobile-nav-title' : ''}
            style={{ textAlign: 'center', backgroundColor: '#f8f9fa' }}
          >
            <Title order={3} c="blue" style={{ fontSize: 'var(--mantine-font-size-xl)' }}>
              進貨統計系統
            </Title>
            <Text size="md" c="dimmed" mt={4}>
              專為便利使用而設計
            </Text>
          </Paper>

          {/* 選單項目 */}
          <Stack gap="xs" style={{ flex: 1 }}>
            {menuItems}
          </Stack>
        </Stack>
      </AppShell.Navbar>

      {/* 主要內容區域 */}
      <AppShell.Main
        style={{
          backgroundColor: '#ffffff',
          overflow: 'auto',
          paddingTop: isMobile ? 84 : 0,
          /* 明確預留桌面版 Navbar 寬度，避免內容被覆蓋 */
          paddingLeft: isMobile ? 12 : (isLarge ? 300 : 280),
          paddingRight: isMobile ? 12 : 0,
          paddingBottom: isMobile ? 16 : 0
        }}
      >
        <ErrorBoundary>
          {isMobile ? (
            /* 手機版：直接渲染內容 */
            <Outlet />
          ) : (
            /* 桌面版：使用Paper包裝 */
            <Paper
              p="xl"
              radius="lg"
              shadow="sm"
              style={{
                backgroundColor: '#ffffff'
              }}
            >
              <Outlet />
            </Paper>
          )}
        </ErrorBoundary>
      </AppShell.Main>

      {/* 底部 - 手機版隱藏 */}
      {!isMobile && (
        <AppShell.Footer p="md">
          <Text ta="center" size="md" c="dimmed">
            進貨統計系統 ©2024 - 專為便利使用而設計
          </Text>
        </AppShell.Footer>
      )}
      </AppShell>
    </AccessibilityEnhancer>
  );
}

export default SeniorFriendlyApp;
